import * as React from "react";
import type { Dispatch } from "redux";
import { connect } from "react-redux";
import type { ReduxState } from "../reducers/reducers";
import * as BusinessLogic from "@/lib/businessLogic";
import {
    INSIGHT_TAGS,
    INSIGHT_TYPES,
    LINE_GROUP_TAGS_ACCESS,
    InsightTypesEnum,
} from "@/lib/ManufacturingTags.generated";
import * as t2 from "@/lib/backend/reports.generated.types";
import * as rt from "@/lib/simulationReportModels";
import * as t from "@/lib/backend/manufacturing2.generated.types";
import { TIME_RANGES, dateFromShiftTime, getCustomOperationTitle, niceNumber } from "@/lib/util";
import { getDarkerColor } from "@/lib/insights";
import {
    ORANGE_COLOR,
    DARK_SALMON_COLOR,
    DARKER_SALMON_COLOR,
    DARKER_DARK_SALMON_COLOR,
    WHITE_COLOR,
    GREY_COLOR,
    LIGHT_BLUE,
    DARK_BLUE,
} from "../colors";
import { translate } from "@/modules/shared/IntlProviderWrapper";
import {
    ReduxFilters,
    PropertiesLogic,
    UserActions,
    ReportLogic,
    LinesOrdersLogic,
    OnOrderClickArgsActionTypes,
} from "../reducers/logic/businessLogic";
import { getCursorValue } from "../reducers/gantt/properties";
import { onMouseEnterDispatch } from "../draggable/DraggableElements";
import OrderTooltip from "./order/OrderTooltip";
import Tooltip from "@/modules/shared/ui/Tooltip";
import { getColorPatternForPlannedOrders } from "@/lib/insights";

import type { IOrderProducedModelEx } from "../reducers/linesOrders";
import type { GanttChartSourceTypes, PropertiesState } from "../reducers/gantt/properties";
import type { AdvancedOrderMenuState, SetErrorAction } from "../reducers/planningTable";
import type { TooltipProperties } from "./order/OrderTooltip";

import img_gantt_downtime_01 from "@/img/gantt_downtime_01.svg";
import img_gantt_downtime_02 from "@/img/gantt_downtime_02.svg";

const IMG_GANTT_DOWNTIME = {
    "1": img_gantt_downtime_01,
    "2": img_gantt_downtime_02,
};

type InsightsRectProps = {
    onInsightClick: (
        order_uuid: string,
        order_external_id: string,
        line_uuid: string,
        process_num: string
    ) => (e: React.MouseEvent<SVGRectElement>) => void;
    is_ctrl_down: boolean;
    is_rescheduled?: boolean;
    order_uuid: string;
    order_external_id: string;
    line_uuid: string;
    process_num: string;
    source_type: GanttChartSourceTypes;
    x: number;
    y: number;
    width: number;
    height: number;
    tooltip_content: React.ReactNode;
    waterfall_mode?: boolean;
};

class InsightsRect extends React.Component<InsightsRectProps> {
    render() {
        const is_rescheduled = this.props.is_rescheduled;
        const width = this.props.width;
        const bar_height = this.props.height;
        const line_uuid = this.props.line_uuid;

        let style: React.CSSProperties = {};
        if (PropertiesLogic.isPlanningTable(this.props.source_type)) {
            style = this.props.is_rescheduled ? { pointerEvents: "none" } : { pointerEvents: "unset" };
        }

        return (
            <Tooltip key={"insights-" + this.props.line_uuid + "-" + this.props.x} content={this.props.tooltip_content}>
                <rect
                    id="insights-rect"
                    x={this.props.x}
                    y={this.props.y}
                    width={width}
                    height={bar_height}
                    fill={"url(#patternDiagonal)"}
                    style={style}
                    cursor={getCursorValue({
                        is_ctrl_down: this.props.is_ctrl_down,
                        is_rescheduled,
                        source_type: this.props.source_type,
                    })}
                    onClick={this.props.onInsightClick(
                        this.props.order_uuid,
                        this.props.order_external_id,
                        line_uuid,
                        this.props.process_num
                    )}
                    // @ts-ignore
                    line_uuid={line_uuid}
                    source_type={this.props.source_type}
                    is_unscheduled={"false"}
                />
            </Tooltip>
        );
    }
}

const ConnectedInsightsRect = connect((state: ReduxState, props: InsightsRectProps) => {
    const is_rescheduled = state.gantt_chart_planning_table.last_clicked_order_uuid === props.order_uuid;
    const waterfall_mode = state.gantt_chart_properties.waterfall_mode;

    return { is_rescheduled, waterfall_mode };
}, null)(InsightsRect);

type InsightsImageProps = {
    is_ctrl_down: boolean;
    is_rescheduled?: boolean;
    source_type: GanttChartSourceTypes;
    order_uuid: string;
    order_external_id: string;
    line_uuid: string;
    process_num: string;
    onInsightClick: (
        order_uuid: string,
        order_external_id: string,
        line_uuid: string,
        process_num: string
    ) => (e: React.MouseEvent) => void;
    xlinkHref: string;
    x: number;
    y: number;
    ts: string;
    tooltip_content: React.ReactNode;
};

class InsightsImage extends React.Component<InsightsImageProps> {
    render() {
        const { is_rescheduled, order_uuid, order_external_id, line_uuid, process_num } = this.props;

        return (
            <Tooltip key={"downtime-" + this.props.line_uuid + "-" + this.props.x} content={this.props.tooltip_content}>
                {/* width/height omitted — icon size comes from the linked SVG's intrinsic dimensions (~14×27) */}
                <image
                    id="gantt-downtime"
                    xlinkHref={this.props.xlinkHref}
                    x={this.props.x}
                    y={this.props.y}
                    cursor={getCursorValue({
                        is_ctrl_down: this.props.is_ctrl_down,
                        is_rescheduled: is_rescheduled ?? false,
                        source_type: this.props.source_type,
                    })}
                    onClick={this.props.onInsightClick(order_uuid, order_external_id, line_uuid, process_num)}
                    onMouseEnter={() => {
                        onMouseEnterDispatch(order_uuid, { name: "gantt-downtime", order_uuid });
                    }}
                    // @ts-ignore
                    ts={this.props.ts}
                ></image>
            </Tooltip>
        );
    }
}

const ConnectedInsightsImage = connect((state: ReduxState, props: InsightsImageProps) => {
    const is_rescheduled = state.gantt_chart_planning_table.last_clicked_order_uuid === props.order_uuid;
    return { is_rescheduled };
}, null)(InsightsImage);

const generateColoredPatterns = (color: string) => {
    const pattern_id = getColorPatternForPlannedOrders(color);
    return (
        <pattern id={pattern_id} x={0} y={-2} width={10} height={10} patternUnits="userSpaceOnUse" key={color}>
            <rect x={0} y={0} width={100} height={100} patternUnits="userSpaceOnUse" fill={color} />
            <circle cx={2.5} cy={2.5} r={2.5} fill={getDarkerColor(color)} fillOpacity={2} />
        </pattern>
    );
};

export const InsightsCommonPatterns = () => {
    return (
        <>
            <pattern
                id="patternCombinedInsights"
                width="12"
                height="14"
                patternUnits="userSpaceOnUse"
                patternTransform="rotate(-37)"
            >
                <line x1="0" y1="0" x2="0" y2="15.5" stroke="#FFB68F" strokeWidth="12"></line>
                <line x1="12" y1="0" x2="12" y2="15.5" stroke="#FF453C" strokeWidth="12"></line>
            </pattern>
            <pattern
                id="patternCombinedPlannedInsights"
                x="-2.5"
                y="-4.5"
                width="20"
                height="10"
                patternUnits="userSpaceOnUse"
                patternTransform="rotate(-37)"
            >
                <rect x="0" y="0" width="10" height="10" fill={ORANGE_COLOR}></rect>
                <circle cx="5" cy="5" r="2.5" fill={DARKER_SALMON_COLOR}></circle>
                <rect x="10" y="0" width="10" height="10" fill={DARK_SALMON_COLOR}></rect>
                <circle cx="15" cy="5" r="2.5" fill={DARKER_DARK_SALMON_COLOR}></circle>
            </pattern>
            <filter id="grayscale">
                <feColorMatrix type="saturate" values="0.20" />
            </filter>
            <pattern id="patternDiagonal" width="4" height="4" patternUnits="userSpaceOnUse">
                <path fill="none" stroke="#00000080" strokeWidth="1" d="M-1,1 l2,-2 M0,4 l4,-4 M3,5 l2,-2"></path>
            </pattern>
            {[
                generateColoredPatterns(ORANGE_COLOR),
                generateColoredPatterns(DARK_SALMON_COLOR),
                generateColoredPatterns(WHITE_COLOR),
                generateColoredPatterns(GREY_COLOR),
                generateColoredPatterns(LIGHT_BLUE),
                generateColoredPatterns(DARK_BLUE),
            ]}
        </>
    );
};

function groupedInsightType(insight_type: string): InsightTypesEnum {
    const insight_mapping = {
        [INSIGHT_TYPES.out_of_stock_plant]: INSIGHT_TYPES.out_of_stock_shopfloor,
    };

    if (insight_mapping[insight_type] !== undefined) {
        return insight_mapping[insight_type];
    }

    return insight_type as InsightTypesEnum;
}

function sameGroupInsights(insights: t.IEventDataEx[], i: number, j: number) {
    const insight_i = insights[i];
    const insight_j = insights[j];
    if (insight_i.tags.line_uuid !== insight_j.tags.line_uuid) {
        return false;
    }
    if (insight_i.tags.order_external_id !== insight_j.tags.order_external_id) {
        return false;
    }
    if (insight_i.tags.shift_tag !== insight_j.tags.shift_tag) {
        return false;
    }
    // merge similar types
    const type_i = groupedInsightType(insight_i.type);
    const type_j = groupedInsightType(insight_j.type);
    if (type_i !== type_j) {
        return false;
    }
    return true;
}

type LineOffets = {
    [key: string]: number;
};

type OwnProps = {
    properties: PropertiesState;
    insights: t2.IEventDataEx[] | null;
    options: rt.ISimulationReportOrderLineEx[];
    line_indexes: LineOffets;
    line_uuid_indexes: LineOffets;
    line_offsets_new?: number[];
};

type Props = OwnProps & ReduxProps;

class Insights extends React.Component<Props> {
    getIsExtraLine = (line_uuid: string): boolean => {
        if (!this.props.extra_lines || this.props.extra_lines.length === 0) {
            return false;
        }

        return this.props.extra_lines.some(l => l.line === line_uuid);
    };

    onInsightClick =
        (order_uuid: string, order_external_id: string, line_uuid: string, process_num: string) =>
        (e: React.MouseEvent) => {
            if (this.props.waterfall_mode) {
                return;
            }

            const currentTarget = e.currentTarget;
            if (currentTarget instanceof Element) {
                const advanced_order_menu: AdvancedOrderMenuState = {
                    order_uuid,
                    click_target: currentTarget,
                    event: e,
                };
                let is_readonly = false;
                const line_order = this.props.lines_orders.find(l => l.line_uuid === line_uuid);
                if (line_order) {
                    const order = line_order.orders.find(o => o.uuid === order_uuid);
                    if (order) {
                        is_readonly = LinesOrdersLogic.isOrderReadOnly(line_order, order);
                    }
                }

                ReduxFilters.last_click_location = "gantt";

                UserActions.onOrderClick({
                    order_uuid,
                    order_external_id,
                    source_type: this.props.source_type,
                    is_extra_line: this.getIsExtraLine(line_uuid),
                    is_ctrl_down: this.props.is_ctrl_down,
                    advanced_order_menu,
                    process_num,
                    reduxDispatch: this.props.reduxDispatch,
                    is_readonly,
                    is_filter_locked: this.props.is_filter_locked,
                    is_multiselected:
                        this.props.multiselect_order_uuids !== null &&
                        this.props.multiselect_order_uuids.includes(order_uuid),
                });

                e.stopPropagation();
            }
        };

    getOrderAndIndex = (
        insight: t2.IEventDataEx
    ): { order: rt.ISimulationReportOrderEx | null; order_index: number } => {
        const line_uuids = ReportLogic.getLineUuidsFromInsight(insight);
        const line_uuid = ReportLogic.getLineUuidFromInsight(insight) || (line_uuids && line_uuids[0]);
        const order_uuids = ReportLogic.getOrderUuidsFromInsight(insight);
        const order_uuid = ReportLogic.getOrderUuidFromInsight(insight) || (order_uuids && order_uuids[0]);
        if (!line_uuid || !order_uuid) {
            return { order: null, order_index: -1 };
        }

        const line = this.props.options.find(l => l.line === line_uuid);
        const order = (line && line.production.find(o => o.order_id === order_uuid)) || null;
        const order_index = (line && line.production.findIndex(o => o.order_id === order_uuid)) || 0;

        return { order, order_index };
    };

    getToolExternalId = (insight: t2.IEventDataEx): string => {
        return (
            (insight.extra_data && insight.extra_data.tool_external_id) ||
            (insight.tags && insight.tags.tool_external_id) ||
            ""
        );
    };

    getTooltipProperties = (
        insight: t2.IEventDataEx,
        immutable_meta_data?: t2.IImmutableMetaData
    ): TooltipProperties[] => {
        const tooltip_properties: TooltipProperties[] = [];
        const tool_external_id = this.getToolExternalId(insight);
        if (tool_external_id) {
            tooltip_properties.push({
                name: translate("common.tool_id", "Tool ID"),
                value: tool_external_id,
            });
        }

        const duration = (insight.ts_to - insight.ts) / TIME_RANGES.HOUR;
        tooltip_properties.push({
            name: translate("common.tool_setup_duration", "Tool setup duration"),
            value: niceNumber(duration, 2) + " h",
        });
        if (immutable_meta_data) {
            const { cleaning_type } = immutable_meta_data;
            if (cleaning_type) {
                tooltip_properties.push({
                    name: translate("common.cleaning_type", "Cleaning type"),
                    value: cleaning_type,
                });
            }
        }

        return tooltip_properties;
    };

    getInsights = () => {
        const properties = this.props.properties;
        const from_shift_time = this.props.from_shift_time;
        const next_shift_time = this.props.next_shift_time;
        if (!this.props.insights || !from_shift_time || !next_shift_time) {
            return null;
        }

        const bars_start = properties.bars_start;
        const xscale = properties.xscale;
        if (!xscale) {
            return null;
        }

        const bar_height = properties.bar_height;

        BusinessLogic.updateTsForInsights(this.props.insights);
        BusinessLogic.updateTsForInsightsToShift(this.props.insights);

        // here we draw only insight for tool setup and downtime, rest are drawn in order bars
        const type_order = [INSIGHT_TYPES.tool_setup, INSIGHT_TYPES.man_downtime].filter(t =>
            this.props.filtered_insight_types.includes(t)
        );
        // handling overlapping alarms
        // sort insights by line, type and timestamp
        let sorted_insights = this.props.insights
            .filter(i => type_order.includes(i.type as InsightTypesEnum))
            .sort((a, b) => {
                // sort by lines and then sort by ts
                if (a.tags.order_uuid > b.tags.order_uuid) {
                    return 1;
                } else if (a.tags.order_uuid < b.tags.order_uuid) {
                    return -1;
                } else {
                    // check if different type to sort over
                    const a_type = type_order.indexOf(groupedInsightType(a.type));
                    const b_type = type_order.indexOf(groupedInsightType(b.type));
                    if (a_type !== b_type) {
                        return a_type - b_type;
                    }
                    // all same, just timestamp difference
                    return a.ts - b.ts;
                }
            });

        const Insights: React.ReactNode[] = [];
        const materials_map: Map<string, t.IMaterialModel> = new Map(this.props.materials.map(m => [m.external_id, m]));
        const base_orders_map: Map<string, t.IOrderModelBase> = new Map(this.props.orders.map(o => [o.uuid, o]));
        const line_orders_map: Map<string, IOrderProducedModelEx> = new Map();
        for (const line_orders of this.props.lines_orders) {
            for (const order of line_orders.orders) {
                line_orders_map.set(order.uuid, order);
            }
        }

        const original_operations_map: Map<string, IOrderProducedModelEx> = new Map(
            this.props.original_operations.map(o => [o.uuid, o])
        );

        for (let i = 0; i < sorted_insights.length; i++) {
            let insight = sorted_insights[i];

            const line_uuid = insight.tags.line_uuid;
            const insights_order_uuid = ReportLogic.getOrderUuidFromInsight(insight);

            const insight_order_uuid = insights_order_uuid;
            if (
                this.props.line_indexes[insight_order_uuid] == undefined &&
                insight.type !== INSIGHT_TYPES.man_downtime
            ) {
                // filters are enabled
                continue;
            }

            if (
                PropertiesLogic.isPlanningTable(this.props.source_type) &&
                (this.props.valid_orders_map === null ||
                    this.props.valid_orders_map.get(insight_order_uuid) === undefined)
            ) {
                continue;
            }

            const from_shift_time_ts = dateFromShiftTime(from_shift_time).getTime();
            const ts = (insight.ts - from_shift_time_ts) / TIME_RANGES.HOUR;

            if (ts < 0) continue;
            if (parseInt(insight.tags.shift_ts, 10) < from_shift_time_ts) {
                continue;
            }

            let heightcounter = 0;
            if (insight.type === INSIGHT_TYPES.man_downtime) {
                heightcounter = this.props.line_uuid_indexes[insight.tags[INSIGHT_TAGS.line_uuid]];
            } else {
                heightcounter = this.props.line_indexes[insight_order_uuid];
            }

            const { order_uuid, order_external_id } = insight.tags;
            const { order, order_index } = this.getOrderAndIndex(insight);

            let space_between_bars = properties.space_between_bars;
            if (this.props.waterfall_mode) {
                space_between_bars = 0;
                heightcounter = order_index;
            }

            const x0 = bars_start + xscale(ts);
            const y0 = properties.top_padding + heightcounter * (bar_height + space_between_bars);

            let process_num = "";
            if (order) {
                process_num = order.process_num.toString();
            }

            const width = xscale((insight.ts_to - insight.ts) / TIME_RANGES.HOUR);
            // draw insight
            if (insight.type === INSIGHT_TYPES.tool_setup) {
                // in the future read this data from simulation
                let insight_tooltips: React.ReactNode[] = [];
                let material: t.IMaterialModel | undefined;
                let order_model: t.IOrderModelBase | IOrderProducedModelEx | undefined;

                if (order && this.props.insights_mapped) {
                    insight_tooltips = (this.props.insights_mapped.get(order.order_id) || []).map(insight =>
                        BusinessLogic.getInsightTooltipContent(insight)
                    );
                    material = materials_map.get(order.material);
                    order_model = line_orders_map.get(order.order_id) || base_orders_map.get(order.order_id);
                }

                const line_group_tags = BusinessLogic.getLineGroupTags(this.props.line_group_uuid);
                const operation_title_template =
                    LINE_GROUP_TAGS_ACCESS.gantt_chart_custom_operation_title_template(line_group_tags);
                const custom_title =
                    order_model !== undefined && material !== undefined
                        ? getCustomOperationTitle(operation_title_template, order_model, material)
                        : "";
                const order_tags = order_model !== undefined ? order_model.tags : {};

                if (width > 0) {
                    const tool_external_id = this.getToolExternalId(insight);
                    const tool_setup_class = order || tool_external_id ? "order-tooltip-title" : "font-weight-500 mb-0";
                    const next_shift_time_ts = dateFromShiftTime(next_shift_time).getTime();
                    const is_historical =
                        order !== null &&
                        (next_shift_time_ts - from_shift_time_ts) / TIME_RANGES.HOUR >
                            order.estimated_completion[1].val;
                    const original_operation = original_operations_map.get(order_uuid);
                    const tooltip_content = (
                        <>
                            <p className={tool_setup_class}>{translate("common.tool_setup", "Tool setup")}</p>
                            {order && (
                                <OrderTooltip
                                    order={order}
                                    unscheduled_order={null}
                                    insight_tooltips={insight_tooltips}
                                    properties={this.getTooltipProperties(insight, order.immutable_meta_data)}
                                    material={material}
                                    tags={order_tags}
                                    custom_title={custom_title}
                                    is_historical={is_historical}
                                    from_shift_time={from_shift_time}
                                    original_earliest_start={original_operation?.earliest_start}
                                    original_earliest_end={original_operation?.earliest_end}
                                    original_initial_earliest_start={original_operation?.initial_earliest_start}
                                    original_initial_earliest_end={original_operation?.initial_earliest_end}
                                />
                            )}
                            {!order && tool_external_id && (
                                <table className="order-tooltip-properties">
                                    <tbody>
                                        <tr>
                                            <th>{translate("common.tool_id", "Tool ID")}</th>
                                            <td>{this.getToolExternalId(insight)}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            )}
                        </>
                    );
                    Insights.push(
                        <ConnectedInsightsRect
                            key={i}
                            onInsightClick={this.onInsightClick}
                            is_ctrl_down={this.props.is_ctrl_down}
                            order_uuid={order_uuid}
                            order_external_id={order_external_id}
                            line_uuid={line_uuid}
                            process_num={process_num}
                            source_type={this.props.source_type}
                            x={x0}
                            y={y0}
                            width={width}
                            height={bar_height}
                            tooltip_content={tooltip_content}
                        />
                    );
                }
            } else if (insight.type === INSIGHT_TYPES.man_downtime) {
                // group similar insights into one tooltip
                const insight_tooltips = [BusinessLogic.getInsightTooltipContent(insight)];
                let insight_duration = insight.extra_data.duration;
                if (i < sorted_insights.length) {
                    let j = 1;
                    while (i + j < sorted_insights.length && sameGroupInsights(sorted_insights, i, i + j)) {
                        insight_tooltips.push(BusinessLogic.getInsightTooltipContent(sorted_insights[i + j]));
                        insight_duration += sorted_insights[i + j].extra_data.duration;
                        j++;
                    }
                    i = i + (j - 1);
                }

                const tooltip_content = (
                    <ul className="order-tooltip-insights list-unstyled mb-0">
                        {insight_tooltips.map((tooltip, i) => {
                            const first_class = i === 0 ? " border-top-0 pt-0 mt-0" : "";
                            return (
                                <li key={i} className={`order-tooltip-section ${first_class}`}>
                                    {tooltip}
                                </li>
                            );
                        })}
                    </ul>
                );

                // select severity
                const insight_severity = insight_duration >= 2.0 ? 2 : 1;
                // add to the gantt
                Insights.push(
                    <ConnectedInsightsImage
                        key={i}
                        xlinkHref={IMG_GANTT_DOWNTIME[insight_severity]}
                        x={x0}
                        y={y0 + 1}
                        ts={new Date(insight.ts).toISOString()}
                        is_ctrl_down={this.props.is_ctrl_down}
                        source_type={this.props.source_type}
                        onInsightClick={this.onInsightClick}
                        order_uuid={order_uuid}
                        order_external_id={order_external_id}
                        line_uuid={line_uuid}
                        process_num={process_num}
                        tooltip_content={tooltip_content}
                    />
                );
            }
        }
        return Insights;
    };

    render() {
        const insights = this.getInsights();

        return (
            <>
                <g id="patterns">
                    <defs>
                        <InsightsCommonPatterns />
                    </defs>
                </g>
                {insights}
            </>
        );
    }
}

const mapState = (state: ReduxState) => {
    const show_valid_lines = state.gantt_chart_planning_table.show_valid_lines;
    const properties = state.gantt_chart_properties;
    const extra_lines = state.gantt_chart_report.extra_lines;
    const planning_table = state.gantt_chart_planning_table;
    const insights_mapped = state.gantt_chart_insights.insights_mapped;
    const filtered_insight_types = state.standalone_gantt.insight_types;
    const lines_orders = state.gantt_chart_lines_orders.lines_orders;
    const is_filter_locked = state.gantt_chart_filters.is_filter_locked;
    const report_data = state.gantt_chart_report.report_data;

    let from_shift_time: t2.ISimulationReportShiftTime | null = null;
    let next_shift_time: t2.ISimulationReportShiftTime | null = null;
    if (report_data) {
        from_shift_time = report_data.result.from_shift_time;
        next_shift_time = report_data.result.next_shift_time;
    }

    const line_group_uuid = state.gantt_chart_properties.line_group_uuid || "";
    const materials = state.gantt_chart.materials;
    const orders = state.gantt_chart.orders;
    const original_operations: IOrderProducedModelEx[] = [];
    const original_lines_orders = state.gantt_chart_lines_orders.original_lines_orders || [];
    const original_unscheduled_orders = state.gantt_chart_lines_orders.original_unscheduled_orders || [];
    const waterfall_mode = state.gantt_chart_properties.waterfall_mode;

    for (const line_orders of [...original_lines_orders, ...original_unscheduled_orders]) {
        for (const order of line_orders.orders) {
            original_operations.push(order);
        }
    }

    return {
        valid_orders_map: show_valid_lines.valid_orders_map,
        lines_orders,
        source_type: properties.source_type,
        extra_lines,
        is_ctrl_down: planning_table.is_ctrl_down,
        insights_mapped,
        filtered_insight_types,
        is_filter_locked,
        from_shift_time,
        next_shift_time,
        materials,
        orders,
        line_group_uuid,
        original_operations,
        multiselect_order_uuids: planning_table.multiselect_order_uuids,
        waterfall_mode,
    };
};

const mapDispatch = (dispatch: Dispatch<OnOrderClickArgsActionTypes | SetErrorAction>) => ({
    reduxDispatch: dispatch,
});

type ReduxProps = ReturnType<typeof mapState> & ReturnType<typeof mapDispatch>;

export default connect<ReturnType<typeof mapState>, ReturnType<typeof mapDispatch>, OwnProps, ReduxState>(
    mapState,
    mapDispatch
)(Insights);
